import React, { useMemo, useState } from 'react';
import '../../../styles/accessibility.css';
import { getEngagementSummary, getPerformanceMetrics, getConversionFunnels, getUserSegments, getABTests } from '../../../services/bread/userEngagementService';
import { getPrefs, updatePrefs } from '../../../services/shared/preferencesService';
import { add as addAuditLog } from '../../../services/bread/auditLogService';
import { trackEvent } from '../../../services/bread/userEngagementService';

/*
  Dashboard Analytics (Bread › Engagement Analytics)
  Industry-style ESG-aligned dashboard consolidation page.
  Focus areas:
   - Executive KPI Strip (7d + ESG overlay)
   - Health Triad (Engagement • Stability • Ethical Alignment proxy)
   - Funnel & Retention snapshot
   - Segment Mix w/ Diversity / Accessibility proxy signals
   - Experiment Efficiency (A/B Test win rate)
*/

function formatNumber(n){ if(n==null) return '—'; if(n>999999) return (n/1_000_000).toFixed(1)+'M'; if(n>999) return (n/1000).toFixed(1)+'K'; return n.toLocaleString(); }
function percent(x, d=1){ return (x*100).toFixed(d)+'%'; }

export default function DashboardAnalytics(){
  const prefs = getPrefs();
  const [esgOverlay, setEsgOverlay] = useState(prefs.engagementDashboardEsg !== false);
  const [showBenchmarks, setShowBenchmarks] = useState(prefs.engagementDashboardBench !== false);

  const summary7 = getEngagementSummary(7) || {};
  const perf = getPerformanceMetrics();
  const funnels = getConversionFunnels();
  const funnel = funnels[0];
  const segments = getUserSegments().slice(0,6);
  const ab = getABTests();

  // Derived KPIs
  const kpis = useMemo(()=> {
    const winTests = ab.filter(t=> t.status==='Running' && t.variants.some(v=> v.conversions>0));
    const expWinRate = winTests.length ? (winTests.filter(t=> t.variants[0].conversions < t.variants[1].conversions).length / winTests.length) : 0;
    const accessibilityProxy = Math.min(100, Math.round((segments.filter(s=> /access|assist|inclusive/i.test(s.name)).length / Math.max(1, segments.length)) * 100));
    const diversityMix = Math.min(100, Math.round((segments.filter(s=> /intl|global|locale|region/i.test(s.name)).length / Math.max(1, segments.length)) * 100));
    return { expWinRate, accessibilityProxy, diversityMix };
  }, [segments, ab]);

  function persistPrefs(){
    updatePrefs({ engagementDashboardEsg: esgOverlay, engagementDashboardBench: showBenchmarks });
  }

  function exportJSON(){
    const payload = { generatedAt: new Date().toISOString(), summary7, perf, funnel, segments, ab, esgOverlay, kpis };
    const blob = new Blob([JSON.stringify(payload,null,2)], { type:'application/json' });
    const url = URL.createObjectURL(blob); const a=document.createElement('a'); a.href=url; a.download='dashboard-analytics.json'; a.click(); URL.revokeObjectURL(url);
  }

  React.useEffect(()=> { trackEvent('page_view', { page: 'bread_engagement_dashboard_analytics' }); addAuditLog({ user:'system', action:'page_access', target:'bread_engagement_dashboard_analytics', status:'success'}); }, []);
  React.useEffect(()=> { persistPrefs(); }, [esgOverlay, showBenchmarks]);

  return (
    <div className="container-fluid py-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
        <div>
          <h2 className="mb-1">Dashboard Analytics</h2>
          <p className="text-muted mb-0">Executive system health & ESG-aligned engagement intelligence</p>
        </div>
        <div className="d-flex gap-2 small align-items-center">
          <a 
            href="https://www.kromarketplace.com/materiality" 
            className="btn btn-primary btn-sm text-white text-decoration-none shadow-sm"
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none',
              fontWeight: '500',
              padding: '0.375rem 1rem',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '';
            }}
            target="_blank"
            rel="noopener noreferrer"
          >
            📊 Data Collection
          </a>
          <div className="form-check form-switch mb-0">
            <input className="form-check-input" type="checkbox" id="benchSwitchD" checked={showBenchmarks} onChange={()=> setShowBenchmarks(v=> !v)} />
            <label className="form-check-label" htmlFor="benchSwitchD">Benchmarks</label>
          </div>
          <div className="form-check form-switch mb-0">
            <input className="form-check-input" type="checkbox" id="esgSwitchD" checked={esgOverlay} onChange={()=> setEsgOverlay(v=> !v)} />
            <label className="form-check-label" htmlFor="esgSwitchD">ESG Overlay</label>
          </div>
          <button className="btn btn-outline-primary btn-sm" onClick={exportJSON}>Export JSON</button>
        </div>
      </div>

      {/* KPI Strip */}
      <div className="row g-3 mb-4">
        <KPI title="Active Users" value={formatNumber(summary7.totalUsers)} accent="primary" />
        <KPI title="Sessions (7d)" value={formatNumber(summary7.totalSessions)} accent="info" />
        <KPI title="Engagement Score" value={summary7.avgEngagementScore} accent="success" />
        <KPI title="Avg Duration" value={Math.round((summary7.avgSessionDuration||0)/60)+'m'} accent="warning" />
        <KPI title="Bounce Rate" value={percent(summary7.avgBounceRate||0,0)} accent="danger" />
        <KPI title="Exp Win Rate" value={percent(kpis.expWinRate,0)} accent="secondary" />
      </div>

      <div className="row g-4">
        <div className="col-xl-5">
          <div className="card shadow-sm h-100">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h6 className="mb-0">Health Triad</h6>
              {esgOverlay && <small className="text-success">ESG Lens Active</small>}
            </div>
            <div className="card-body">
              <HealthGauge label="Engagement" value={summary7.avgEngagementScore||0} target={70} />
              <HealthGauge label="Stability" value={(100-(summary7.avgBounceRate||0)*100)} target={65} invert />
              <HealthGauge label="Ethical Alignment" value={(kpis.accessibilityProxy + kpis.diversityMix)/2} target={60} />
              {showBenchmarks && <p className="small text-muted mb-0 mt-2">Benchmarks: Engagement ≥70 • Stability ≥65 • Ethical ≥60</p>}
            </div>
          </div>
        </div>
        <div className="col-xl-7">
          <div className="card shadow-sm h-100">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h6 className="mb-0">Primary Funnel Snapshot</h6>
              <small className="text-muted">{funnel?.name}</small>
            </div>
            <div className="card-body">
              {!funnel ? <p className="text-muted small mb-0">No funnel data</p> : funnel.steps.map((s,i)=>{
                const pct = Math.round(s.conversionRate*100);
                return (
                  <div key={i} className="mb-3">
                    <div className="d-flex justify-content-between small mb-1"><span>{s.name}</span><span>{formatNumber(s.users)} • {pct}%</span></div>
                    <div className="progress" style={{height:6}}>
                      <div className={`progress-bar bg-${pct>70?'success': pct>50?'warning':'danger'}`} style={{width:pct+'%'}} />
                    </div>
                  </div>
                );
              })}
              {esgOverlay && <div className="alert alert-light border mt-2 py-2 small mb-0"><strong>Alignment:</strong> Funnel stages reflect consent + transparency commitments (simulated).</div>}
            </div>
          </div>
        </div>
      </div>

      <div className="row g-4 mt-1">
        <div className="col-lg-6">
          <div className="card shadow-sm h-100">
            <div className="card-header d-flex justify-content-between align-items-center"><h6 className="mb-0">Segment Mix</h6><small className="text-muted">Top 6</small></div>
            <div className="card-body p-0">
              <ul className="list-group list-group-flush small">
                {segments.map((seg,i)=> (
                  <li key={i} className="list-group-item d-flex justify-content-between align-items-center">
                    <span className="d-flex align-items-center gap-2"><span style={{background:seg.color,width:10,height:10,borderRadius:2,display:'inline-block'}} />{seg.name}</span>
                    <span className="text-muted">{seg.size.toLocaleString()}</span>
                  </li>
                ))}
              </ul>
              {esgOverlay && <p className="small text-muted m-2 mb-0">Diversity Mix {kpis.diversityMix}% • Accessibility Proxy {kpis.accessibilityProxy}%</p>}
            </div>
          </div>
        </div>
        <div className="col-lg-6">
          <div className="card shadow-sm h-100">
            <div className="card-header d-flex justify-content-between align-items-center"><h6 className="mb-0">Experiment Efficiency</h6><small className="text-muted">{ab.length} active</small></div>
            <div className="card-body p-0">
              <ul className="list-group list-group-flush small">
                {ab.map((t,i)=> (
                  <li key={i} className="list-group-item">
                    <div className="d-flex justify-content-between">
                      <strong>{t.name}</strong>
                      <span className={`badge ${t.status==='Running'?'bg-success':'bg-secondary'}`}>{t.status}</span>
                    </div>
                    <div className="mt-1 d-flex flex-wrap gap-2">
                      {t.variants.map((v,j)=> (
                        <span key={j} className="border rounded px-2 py-1 bg-light">{v.name}: {Math.round(v.conversions/Math.max(1,v.visitors)*100)}%</span>
                      ))}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}

function KPI({ title, value, accent='primary' }){
  return (
    <div className="col-6 col-md-4 col-xl-2">
      <div className={`border rounded shadow-sm p-3 h-100 position-relative bg-white` }>
        <div className="small text-uppercase text-muted fw-semibold mb-1">{title}</div>
        <div className={`h4 mb-0 fw-semibold text-accent-${accent}`}>{value==null? '—': value}</div>
      </div>
    </div>
  );
}

function HealthGauge({ label, value, target, invert=false }){
  const pct = Math.max(0, Math.min(100, invert? value : value));
  const good = pct >= target;
  return (
    <div className="mb-3">
      <div className="d-flex justify-content-between small mb-1"><span>{label}</span><span>{pct}%</span></div>
        <div className="progress" style={{height:6}}>
        <div className={`progress-bar bg-${good?'success':'warning'}`} style={{width: pct+'%'}} aria-label={`${label} ${pct}%`} />
      </div>
    </div>
  );
}
